import { $http } from "@/plugins/api";
import { getApiClientUrl } from "@/config/runtimeConfig";
import type {
  ConsumerOrganisationLicencesResponse,
  LicenceWithSeats,
  ResellerOrganisationResponse,
} from "@/types/licenceSolutions";

const inflight: Record<string, Promise<unknown> | undefined> = {};
const cache: Record<string, unknown> = {};

const normalizeSeatsUsed = <
  T extends { seats_used?: number; seats_used_total?: number },
>(
  item: T,
): T => ({
  ...item,
  // For org-licence responses, v3 exposes seats_used as self usage and
  // seats_used_total as aggregate usage including descendants/customers.
  // The UI currently displays a single "active users" number and does not
  // distinguish self vs aggregate usage, so normalize seats_used to the
  // larger of the two values.
  seats_used: Math.max(item.seats_used ?? 0, item.seats_used_total ?? 0),
});

const normalizeLicencesForOrgResponse = (
  data: ConsumerOrganisationLicencesResponse | ResellerOrganisationResponse,
) => {
  const normalizedData =
    "seats_used" in data ? normalizeSeatsUsed(data) : { ...data };

  if (
    !("licences" in normalizedData) ||
    !Array.isArray(normalizedData.licences)
  ) {
    return normalizedData;
  }

  return {
    ...normalizedData,
    licences: normalizedData.licences.map((licence) =>
      "seats_used" in licence
        ? normalizeSeatsUsed(licence as LicenceWithSeats)
        : licence,
    ),
  };
};

export const getLicencesForOrg = async (uuid: string) => {
  if (!uuid) throw new Error("Organisation ID is not provided");

  if (cache[uuid]) return cache[uuid];
  if (inflight[uuid]) return inflight[uuid];

  inflight[uuid] = $http
    .get(`${getApiClientUrl()}/v2/licence/orgs/${uuid}`)
    .then((res) => {
      cache[uuid] = normalizeLicencesForOrgResponse(res.data);
      return cache[uuid];
    })
    .finally(() => {
      delete inflight[uuid];
    });

  return inflight[uuid];
};

export const clearLicencesCache = () => {
  Object.keys(cache).forEach((k) => delete cache[k]);
};

export default {
  getLicencesForOrg,
  clearLicencesCache,
};
